/**
 * "Export orders" card on /billing/school (PROD-1378).
 *
 * A school reconciling against its caterer's invoice wants one sentence —
 * "385 breakfast orders, $847.00, 9/28/26–10/2/26" — and the line detail
 * behind it. Pick a service-date range, optionally a campus and a mealtime,
 * read the headline, download the CSV.
 *
 * The preview query and the CSV are the SAME endpoint pair over the SAME
 * filter, so the number on screen is the number in the file. Nothing loads
 * until a valid range is set — an export reads orders as they are now, and
 * an unbounded default range on page load would be a district-wide scan
 * nobody asked for.
 *
 * Rendered by BOTH billing views: a balance load-up district has no
 * invoices at all, and this is the only reconciliation artifact it gets.
 */

import { Button } from '@ordo/ui/button'
import { Card, CardContent, CardHeader } from '@ordo/ui/card'
import { Input } from '@ordo/ui/input'
import { Label } from '@ordo/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ordo/ui/select'
import { toast } from '@ordo/ui/sonner'
import { Download } from 'lucide-react'
import { useMemo, useState } from 'react'

import {
  orderReconciliationCsvUrl,
  type OrderReconciliationFilter,
  type OrderReconciliationSummary,
  useOrderReconciliationQuery,
} from '@/api/billing-school'
import { authHeaders, saveBlob } from '@/api/http'
import { formatCents } from '@/components/billing/helpers'
import { fetchAuthedCsv } from '@/lib/authed-pdf-download'
import { type Mealtime, MEALTIME_LABELS, MEALTIMES } from '@/lib/mealtime'

/** Matches the server guard (`ORDER_RECONCILIATION_MAX_RANGE_DAYS`). */
const MAX_RANGE_DAYS = 400

const ALL = 'all'

export interface OrderReconciliationCardSchool {
  locationId: string
  locationName: string
}

export interface OrderReconciliationCardProps {
  /** Campuses the admin may export. A single campus hides the picker. */
  permittedSchools: readonly OrderReconciliationCardSchool[]
}

/** `2026-09-28` → `9/28/26`, the shorthand the partner's own report used. */
export function formatShortDate(iso: string): string {
  const [year, month, day] = iso.split('-')
  if (!year || !month || !day) {
    return iso
  }
  return `${Number(month)}/${Number(day)}/${year.slice(2)}`
}

/** Inclusive day count, or null when either end is missing. */
export function rangeSpanDays(from: string, to: string): number | null {
  if (!from || !to) {
    return null
  }
  const start = Date.parse(`${from}T00:00:00Z`)
  const end = Date.parse(`${to}T00:00:00Z`)
  if (Number.isNaN(start) || Number.isNaN(end)) {
    return null
  }
  return Math.round((end - start) / 86_400_000) + 1
}

/** The reason the range is unusable, or null when it is good to query. */
export function rangeProblem(from: string, to: string): string | null {
  const span = rangeSpanDays(from, to)
  if (span === null) {
    return null
  }
  if (span <= 0) {
    return 'The end date is before the start date.'
  }
  if (span > MAX_RANGE_DAYS) {
    return `Pick a range of ${MAX_RANGE_DAYS} days or fewer.`
  }
  return null
}

/**
 * The headline sentence — "385 breakfast orders, $847.00, 9/28/26–10/2/26".
 * `itemCount` is the meal count, which is the number the school recognizes;
 * `orderCount` (the batches those meals were placed in) is reported beside
 * it rather than instead of it, since the two differ for a multi-campus
 * range and conflating them is exactly the confusion this export removes.
 */
export function summarySentence(
  summary: OrderReconciliationSummary,
  filter: { from: string; to: string; mealtime: Mealtime | null },
): string {
  const meal = filter.mealtime ? `${MEALTIME_LABELS[filter.mealtime]} ` : ''
  const items = `${summary.itemCount.toLocaleString()} ${meal}${summary.itemCount === 1 ? 'item' : 'items'}`
  const money = formatCents(summary.totalCents, summary.currency)
  return `${items}, ${money}, ${formatShortDate(filter.from)}–${formatShortDate(filter.to)}`
}

export function OrderReconciliationCard({ permittedSchools }: OrderReconciliationCardProps) {
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [campus, setCampus] = useState<string>(ALL)
  const [mealtime, setMealtime] = useState<Mealtime | typeof ALL>(ALL)
  const [downloading, setDownloading] = useState(false)

  const problem = rangeProblem(from, to)
  const filter = useMemo<OrderReconciliationFilter | null>(
    () =>
      from && to && !rangeProblem(from, to)
        ? {
            from,
            to,
            schoolLocationId: campus === ALL ? null : campus,
            mealtime: mealtime === ALL ? null : mealtime,
          }
        : null,
    [from, to, campus, mealtime],
  )

  const { data, isFetching, error } = useOrderReconciliationQuery(filter)

  async function download(): Promise<void> {
    if (!filter) {
      return
    }
    setDownloading(true)
    const toastId = toast.loading('Preparing the export…')
    const result = await fetchAuthedCsv(orderReconciliationCsvUrl(filter), {
      fallbackFilename: 'order-reconciliation.csv',
      headers: authHeaders(),
    })
    setDownloading(false)
    if (result.ok) {
      toast.success('Export ready', { id: toastId })
      saveBlob(result.blob, result.filename)
      return
    }
    toast.error("Couldn't build the export", {
      id: toastId,
      description: result.message,
      action: { label: 'Retry', onClick: () => void download() },
    })
  }

  return (
    <Card>
      <CardHeader className="flex flex-col gap-1.5">
        <h3 className="text-lg font-semibold tracking-tight">Export orders</h3>
        <p className="text-xs text-muted-foreground">
          Pick a service-date range for a reconciliation report — what was ordered, what it cost, and the totals.
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="order-export-from">From</Label>
            <Input
              id="order-export-from"
              type="date"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
              className="w-40"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="order-export-to">To</Label>
            <Input
              id="order-export-to"
              type="date"
              value={to}
              onChange={(event) => setTo(event.target.value)}
              className="w-40"
            />
          </div>

          {permittedSchools.length > 1 ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="order-export-campus">Campus</Label>
              <Select value={campus} onValueChange={setCampus}>
                <SelectTrigger id="order-export-campus" className="w-56">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>All campuses</SelectItem>
                  {permittedSchools.map((school) => (
                    <SelectItem key={school.locationId} value={school.locationId}>
                      {school.locationName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="order-export-mealtime">Mealtime</Label>
            <Select value={mealtime} onValueChange={(next) => setMealtime(next as Mealtime | typeof ALL)}>
              <SelectTrigger id="order-export-mealtime" className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All mealtimes</SelectItem>
                {MEALTIMES.map((value) => (
                  <SelectItem key={value} value={value}>
                    {MEALTIME_LABELS[value]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Button
            type="button"
            variant="outline"
            disabled={!filter || downloading || isFetching}
            onClick={() => void download()}>
            <Download className="size-4" aria-hidden />
            Download CSV
          </Button>
        </div>

        <p className="text-sm" aria-live="polite">
          {problem ? (
            <span className="text-destructive">{problem}</span>
          ) : error ? (
            <span className="text-destructive">{error.message}</span>
          ) : !filter ? (
            <span className="text-muted-foreground">Pick a start and end date to see the totals.</span>
          ) : isFetching ? (
            <span className="text-muted-foreground">Counting orders…</span>
          ) : data ? (
            <span className="tabular-nums">
              {summarySentence(data.summary, {
                from: filter.from,
                to: filter.to,
                mealtime: mealtime === ALL ? null : mealtime,
              })}
              <span className="text-muted-foreground">
                {' '}
                · {data.summary.orderCount.toLocaleString()} {data.summary.orderCount === 1 ? 'order' : 'orders'}
              </span>
            </span>
          ) : null}
        </p>
      </CardContent>
    </Card>
  )
}
